/**
 * Player movement — US 01.01, pure. The world layer feeds it intent frames
 * and a tick duration; it returns the new kinematic state. No Phaser, no DOM.
 *
 * Model: velocity ramps toward `WALK_SPEED * move` at the acceleration rate
 * and decays to zero at the (faster) stop rate, hitting the story's numbers:
 * full speed in ≤100 ms, standstill in ≤80 ms.
 */
import {
  ACCEL_TIME_MS,
  COLLIDER_H,
  COLLIDER_W,
  STOP_TIME_MS,
  TILE,
  WALK_SPEED,
} from '@shared/constants.js';
import type { MoveVector } from '@shared/input.js';
import type { Facing, MoveMode, TileGrid } from './grid.js';
import { isWalkable } from './grid.js';

export interface PlayerState {
  /** Bottom-centre anchor, world px (the feet). */
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: Facing;
}

const ACCEL = WALK_SPEED / (ACCEL_TIME_MS / 1000); // px/s²
const DECEL = WALK_SPEED / (STOP_TIME_MS / 1000); // px/s² — stopping is faster

/** Moves one velocity component toward its target at the given rate. */
function approach(current: number, target: number, rate: number, dt: number): number {
  const delta = rate * dt;
  if (current < target) return Math.min(current + delta, target);
  if (current > target) return Math.max(current - delta, target);
  return current;
}

/**
 * Facing follows the last direction moved and persists when stationary
 * (US 01.01). On diagonal input the dominant axis wins; on a perfect
 * diagonal the horizontal reads better for the side-on sprite set.
 */
export function facingFromMove(move: MoveVector, previous: Facing): Facing {
  if (move.x === 0 && move.y === 0) return previous;
  if (Math.abs(move.x) >= Math.abs(move.y)) return move.x > 0 ? 'right' : 'left';
  return move.y > 0 ? 'down' : 'up';
}

/**
 * The collision box is 32×24 anchored bottom-centre (owner decision):
 * narrower than a tile so doorways pass cleanly, short so depth-sorting
 * reads correctly. (x, y) is the anchor — the box extends up from the feet.
 */
function boxCorners(x: number, y: number): { cx: number; cy: number }[] {
  const left = x - COLLIDER_W / 2;
  const right = x + COLLIDER_W / 2 - 1; // inclusive edge, avoids phantom next-tile hits
  const top = y - COLLIDER_H;
  const bottom = y - 1;
  return [
    { cx: left, cy: top },
    { cx: right, cy: top },
    { cx: left, cy: bottom },
    { cx: right, cy: bottom },
  ];
}

function boxCollides(grid: TileGrid, x: number, y: number, mode: MoveMode): boolean {
  return boxCorners(x, y).some(
    ({ cx, cy }) => !isWalkable(grid, Math.floor(cx / TILE), Math.floor(cy / TILE), mode),
  );
}

/**
 * Resolves one axis of movement: advance, and on collision snap flush to the
 * tile boundary. Resolving axes separately is what makes the player slide
 * along walls instead of sticking (US 01.01).
 */
function resolveAxis(
  grid: TileGrid,
  x: number,
  y: number,
  delta: number,
  axis: 'x' | 'y',
  mode: MoveMode,
) {
  if (delta === 0) return axis === 'x' ? x : y;
  const nx = axis === 'x' ? x + delta : x;
  const ny = axis === 'y' ? y + delta : y;
  if (!boxCollides(grid, nx, ny, mode)) return axis === 'x' ? nx : ny;

  // Snap flush against the blocking tile edge.
  if (axis === 'x') {
    const half = COLLIDER_W / 2;
    if (delta > 0) {
      const edge = Math.floor((x + half - 1 + delta) / TILE) * TILE;
      return edge - half;
    }
    const edge = Math.floor((x - half + delta) / TILE + 1) * TILE;
    return edge + half;
  }
  if (delta > 0) {
    return Math.floor((y - 1 + delta) / TILE) * TILE;
  }
  const edge = Math.floor((y - COLLIDER_H + delta) / TILE + 1) * TILE;
  return edge + COLLIDER_H;
}

/**
 * Advances the player one fixed tick. `dt` in seconds. `speedMultiplier`
 * scales top speed AND the accel/decel rates together (bicycle ×2 — 108),
 * so the ramp-up feel is identical at any speed.
 */
export function stepPlayer(
  state: PlayerState,
  move: MoveVector,
  grid: TileGrid,
  dt: number,
  speedMultiplier = 1,
  mode: MoveMode = 'foot',
): PlayerState {
  const targetVx = WALK_SPEED * speedMultiplier * move.x;
  const targetVy = WALK_SPEED * speedMultiplier * move.y;

  // Accelerate toward an active target; decelerate toward zero when released.
  const accel = ACCEL * speedMultiplier;
  const decel = DECEL * speedMultiplier;
  const vx = approach(state.vx, targetVx, move.x !== 0 ? accel : decel, dt);
  const vy = approach(state.vy, targetVy, move.y !== 0 ? accel : decel, dt);

  // Per-axis resolution: x first, then y, each against the already-resolved
  // position of the other. Order is irrelevant for axis-aligned tile walls.
  const x = resolveAxis(grid, state.x, state.y, vx * dt, 'x', mode);
  const y = resolveAxis(grid, x, state.y, vy * dt, 'y', mode);

  return {
    x,
    y,
    // A wall zeroes the velocity on that axis — otherwise releasing the key
    // after pushing a wall would "launch" the player from stored speed.
    vx: x === state.x + vx * dt ? vx : 0,
    vy: y === state.y + vy * dt ? vy : 0,
    facing: facingFromMove(move, state.facing),
  };
}

/** Collision-box centre — what interaction zones test (US 01.02). */
export function boxCentre(state: PlayerState): { x: number; y: number } {
  return { x: state.x, y: state.y - COLLIDER_H / 2 };
}
